import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Box, Button, Tab, Tabs, Typography } from '@mui/material';

import {
	WalletOperation,
	WalletOperationCriteria,
	WalletOperationQueue,
	WalletOperationSearch,
	WalletOperationSortField,
	WalletOperationSortOrder,
	emptyWalletOperationCriteria,
	walletOperationQueues,
} from 'lib/models/walletOperations';

import useWalletOperations from 'hooks/useWalletOperations';
import useWalletOperationLabels from 'hooks/useWalletOperationLabels';

import ScreenContainer from 'components/containers/ScreenContainer';
import WalletOperationsFilters from 'components/WalletOperationsFilters';
import WalletOperationsTable from 'components/WalletOperationsTable';
import WalletOperationDetailModal from 'components/WalletOperationDetailModal';

const DEFAULT_ROWS_PER_PAGE = 25;

/** Newest first, which is the order the endpoint answers in when nobody asked for another. */
const DEFAULT_SORT: WalletOperationSortField = 'createdAt';
const DEFAULT_ORDER: WalletOperationSortOrder = 'desc';

/**
 * The operations a person has to resolve, and the ones somebody already did.
 *
 * The four tabs are the four questions this screen is opened with: what needs a decision now
 * (the default, and what the screen has always shown), what the reconciler is still
 * confirming, what stopped being open and how it was closed, and everything at once for when
 * nobody knows yet which of those the customer on the phone is asking about.
 *
 * Every narrowing, the order and the window are the endpoint's own: they are sent to it and
 * never applied to the page it answered with, because a hundred rows sorted in the browser
 * are the order of a hundred rows and not of the collection.
 */
function WalletOperations() {
	const { t } = useTranslation();
	const { queueLabel, queueHelp } = useWalletOperationLabels();

	const [queue, setQueue] = useState<WalletOperationQueue>('review');
	const [criteria, setCriteria] = useState<WalletOperationCriteria>(emptyWalletOperationCriteria);
	const [sort, setSort] = useState<WalletOperationSortField>(DEFAULT_SORT);
	const [order, setOrder] = useState<WalletOperationSortOrder>(DEFAULT_ORDER);
	const [page, setPage] = useState<number>(0);
	const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
	const [openId, setOpenId] = useState<string | null>(null);

	// The hook re-reads whenever this changes, so it is memoised on its own values rather
	// than rebuilt every render.
	const search = useMemo<WalletOperationSearch>(
		() => ({ ...criteria, queue, sort, order, page, rowsPerPage }),
		[criteria, queue, sort, order, page, rowsPerPage]
	);

	const { loading, operations, total, totalRelation, failed, refetch } = useWalletOperations(search);

	function onOpen(operation: WalletOperation) {
		setOpenId(operation.id);
	}

	function onQueueChange(next: WalletOperationQueue) {
		setQueue(next);
		setPage(0);
	}

	function onSearch(next: WalletOperationCriteria) {
		setCriteria(next);
		setPage(0);
	}

	/**
	 * Orders the listing by a column, or turns it around when it already was.
	 *
	 * A column the table was not ordered by starts descending: the newest operation and the
	 * largest amount are what the person looking for one starts from.
	 */
	function onSortChange(field: WalletOperationSortField) {
		if (field === sort) {
			setOrder(order === 'asc' ? 'desc' : 'asc');
		} else {
			setSort(field);
			setOrder('desc');
		}

		setPage(0);
	}

	return (
		<ScreenContainer title={t('common:walletOperations')}>
			<Typography variant="body2" sx={{ paddingBottom: 2 }}>
				{t('common:walletOperationsSubtitle')}
			</Typography>

			<Tabs
				value={queue}
				onChange={(_, next: WalletOperationQueue) => onQueueChange(next)}
				variant="scrollable"
				sx={{ marginBottom: 1 }}
			>
				{walletOperationQueues.map((option) => (
					<Tab key={option} value={option} label={queueLabel(option)} />
				))}
			</Tabs>

			<Typography variant="caption" sx={{ display: 'block', paddingBottom: 1 }}>
				{queueHelp(queue)}
			</Typography>

			<WalletOperationsFilters onSearch={onSearch} loading={loading} />

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, paddingY: 1 }}>
				<Button onClick={refetch} disabled={loading}>
					{t('common:reload')}
				</Button>

				{totalRelation === 'gte' && (
					<Typography variant="caption">{t('common:walletOperationsTotalFloor', { total })}</Typography>
				)}
			</Box>

			{failed && (
				<Alert severity="error" sx={{ marginBottom: 1 }}>
					{t('common:walletOperationsFailed')}
				</Alert>
			)}

			<WalletOperationsTable
				loading={loading}
				operations={operations}
				onOpen={onOpen}
				total={total}
				page={page}
				rowsPerPage={rowsPerPage}
				sort={sort}
				order={order}
				onSortChange={onSortChange}
				onPageChange={setPage}
				onRowsPerPageChange={(next) => {
					setRowsPerPage(next);
					setPage(0);
				}}
			/>

			<WalletOperationDetailModal operationId={openId} onClose={() => setOpenId(null)} onResolved={refetch} />
		</ScreenContainer>
	);
}

export default WalletOperations;
